import React, { useCallback, useMemo, useState } from "react";
import { InteractionMode, TreeItem, TreeItemIndex } from "react-complex-tree";

import { Tree } from "ui";

import { selectVersionConfig } from "fond/api";
import { LegendItemType } from "fond/map/Legend/Legend";
import { generateTreeItems } from "fond/utils/configurations";
import { useAppSelector } from "fond/utils/hooks";

import ItemTitle from "./ItemTitle";
import { FeatureListTypes } from ".";

interface LayerSelectionProps {
  type: FeatureListTypes;
  onChange: (items: TreeItemIndex[]) => void;
}

const LayerSelection: React.FC<LayerSelectionProps> = ({ onChange, type }: LayerSelectionProps) => {
  const versionId = useAppSelector((state) => state.project.versionId);
  const config = useAppSelector((state) => {
    const configuration = selectVersionConfig(state, versionId);
    const mapChildren = configuration.MapChildren.filter((id) => {
      const entity = configuration.Data.entities[id];
      const isErrorGroup = entity?.Type === "GROUP" && entity.Key === "error";

      // Filter out project comments and ensure the correct type of layers are displayed
      if (id === "projectComments") return false;
      if (type === FeatureListTypes.DESIGN && isErrorGroup) return false;
      if (type === FeatureListTypes.ERRORS && !isErrorGroup) return false;
      return true;
    });
    return { ...configuration, MapChildren: mapChildren };
  });
  const treeItems = useMemo(() => generateTreeItems({ config, showStyles: false }), [config]);
  const [expandedItems, setExpandedItems] = useState<TreeItemIndex[]>(config.MapChildren);
  const [selectedItems, setSelectedItems] = useState<TreeItemIndex[]>([]);
  const [focusedItem, setFocusedItem] = useState<TreeItemIndex>();

  const getItemTitle = useCallback(({ data: itemData }: TreeItem<LegendItemType>) => {
    if (itemData.Type === "STYLE") return itemData.Name;
    if (itemData.Type === "MapLayerConfig") return "";
    return itemData.Label;
  }, []);

  const renderItemTitle = useCallback(
    (renderProps: { item: TreeItem<LegendItemType | null>; title: string }) => <ItemTitle {...renderProps} config={config} />,
    [config]
  );

  const handleOnExpand = useCallback((item: TreeItem<LegendItemType | null>) => {
    setExpandedItems((prev) => [...prev, item.index]);
  }, []);

  const handleOnCollapse = useCallback((item: TreeItem<LegendItemType | null>) => {
    setExpandedItems((prev) => prev?.filter((expandedItemIndex) => expandedItemIndex !== item.index));
  }, []);

  const handleOnSelect = useCallback(
    (items: TreeItemIndex[]) => {
      setSelectedItems(items.slice(0, 1)); // Limit to one selected item
      onChange(items);
    },
    [onChange]
  );

  const handleOnFocus = useCallback((item: TreeItem<LegendItemType | null>) => setFocusedItem(item.index), []);

  return (
    <Tree<LegendItemType | null>
      autoFocus={false}
      items={treeItems}
      mode="controlled"
      getItemTitle={getItemTitle}
      getItemClass={(item) => `rct-tree-item-button-${item.data?.Type}`}
      renderItemTitle={renderItemTitle}
      onCollapseItem={handleOnCollapse}
      onExpandItem={handleOnExpand}
      onSelectItems={handleOnSelect}
      // We customize the interaction mode to only allow selection
      // of layers and sublayers, not groups & prevent the default toggle behavior.
      defaultInteractionMode={{
        mode: "custom",
        extends: InteractionMode.ClickArrowToExpand,
        createInteractiveElementProps: (item, _, actions, renderFlags) => ({
          onClick: () => {
            actions.focusItem();
            if (item.data?.Type === "GROUP") return;
            actions.selectItem();
          },
          onFocus: () => {
            actions.focusItem();
          },
          tabIndex: renderFlags.isFocused ? 0 : -1,
        }),
      }}
      onFocusItem={handleOnFocus}
      viewState={{
        expandedItems,
        selectedItems,
        focusedItem,
      }}
    />
  );
};

export default LayerSelection;
